import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid2 as Grid,
  Chip,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  GroupWork as GroupIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { settingsService, EntityGroup } from '../../services';

interface GroupStats {
  total: number;
  ativos: number;
  inativos: number;
}

const EntityGroups: React.FC = () => {
  const { t } = useTranslation();
  const [groups, setGroups] = useState<EntityGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState<GroupStats>({ total: 0, ativos: 0, inativos: 0 });
  const [editDialog, setEditDialog] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<EntityGroup | null>(null);
  const [formData, setFormData] = useState({
    codigo: '',
    nome: '',
    descricao: '',
    estado: 'A',
  });

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await settingsService.getEntityGroups({
        page: page + 1,
        limit: rowsPerPage,
      });
      setGroups(response.data || []);
      setTotalCount(response.pagination?.total || response.data?.length || 0);
      if (response.stats) {
        setStats(response.stats);
      }
    } catch (err: any) {
      console.error('Error fetching entity groups:', err);
      setError(err.message || t('entityGroups.errorLoading'));
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const getStatusChip = (estado: string) => {
    return estado === 'A' ? (
      <Chip size="small" color="success" label={t('common.active')} icon={<ActiveIcon />} />
    ) : (
      <Chip size="small" color="default" label={t('common.inactive')} icon={<InactiveIcon />} />
    );
  };

  const handleOpenEdit = (group?: EntityGroup) => {
    if (group) {
      setSelectedGroup(group);
      setFormData({
        codigo: group.codigo || '',
        nome: group.nome,
        descricao: group.descricao || '',
        estado: group.estado,
      });
    } else {
      setSelectedGroup(null);
      setFormData({
        codigo: '',
        nome: '',
        descricao: '',
        estado: 'A',
      });
    }
    setEditDialog(true);
  };

  const handleSave = async () => {
    try {
      setError(null);
      if (selectedGroup) {
        await settingsService.updateEntityGroup(selectedGroup.id, {
          nome: formData.nome,
          descricao: formData.descricao,
          estado: formData.estado,
        });
      } else {
        await settingsService.createEntityGroup({
          codigo: formData.codigo,
          nome: formData.nome,
          descricao: formData.descricao,
          estado: formData.estado,
        });
      }
      setEditDialog(false);
      setSelectedGroup(null);
      fetchGroups();
    } catch (err: any) {
      setError(err.message || t('entityGroups.errorSaving'));
    }
  };

  const handleDelete = async (group: EntityGroup) => {
    if (!window.confirm(t('entityGroups.confirmDelete', { name: group.nome }))) {
      return;
    }

    try {
      setError(null);
      await settingsService.deleteEntityGroup(group.id);
      fetchGroups();
    } catch (err: any) {
      setError(err.message || t('entityGroups.errorDeleting'));
    }
  };

  if (loading && groups.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          {t('entityGroups.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchGroups}>
            {t('common.refresh')}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
            {t('entityGroups.newGroup')}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 4 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GroupIcon color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('entityGroups.totalGroups')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ActiveIcon color="success" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.ativos}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('entityGroups.active')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InactiveIcon color="disabled" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.inativos}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('entityGroups.inactive')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('common.code')}</TableCell>
                <TableCell>{t('common.name')}</TableCell>
                <TableCell>{t('common.description')}</TableCell>
                <TableCell align="center" width={120}>{t('common.entity')}</TableCell>
                <TableCell width={120}>{t('common.status')}</TableCell>
                <TableCell width={120} align="center">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {t('entityGroups.noGroups')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                groups.map((group) => (
                  <TableRow key={group.id} hover>
                    <TableCell>
                      <Chip size="small" label={group.codigo} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {group.nome}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {group.descricao || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip size="small" label={group.membrosCount || 0} color="primary" />
                    </TableCell>
                    <TableCell>{getStatusChip(group.estado)}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleOpenEdit(group)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(group)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage={t('common.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t('common.of')} ${count}`}
        />
      </Paper>

      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupIcon />
          {selectedGroup ? t('entityGroups.editGroup') : t('entityGroups.newGroup')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('common.code')}
                value={formData.codigo}
                onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toUpperCase() })}
                disabled={!!selectedGroup}
                placeholder={t('entityGroups.codePlaceholder')}
                helperText={selectedGroup ? t('entityGroups.codeHelper') : ''}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('common.name')}
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('common.description')}
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                select
                label={t('common.status')}
                value={formData.estado}
                onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
              >
                <MenuItem value="A">{t('common.active')}</MenuItem>
                <MenuItem value="I">{t('common.inactive')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.nome || (!selectedGroup && !formData.codigo)}
          >
            {selectedGroup ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EntityGroups;
